import {useMutation, useQueryClient} from "react-query";
import Alert from "react-s-alert";

import {TableUserDto} from "../../../../../utils/types/user";
import {UserService} from "../../../../../services/UserService";
import {QuicksightService} from "../../../../../services/QuicksightService";
import {buildResponseErrorMessage} from "../../../../../services/api/errorHelpers";
import {fetchRevokeAllDashboardAssignments} from "../../permissions-bulk-edit/bulkQuicksightDashboards.helpers";
import {invalidateQuicksightDashboardCachesForEmails} from "../../../hooks/quicksightUsersDashboards.helpers";

/** Removes reporting permissions and Quicksight dashboard access from multiple users. */
export const useBulkRemoveUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (users: ReadonlyArray<TableUserDto>) => {
      const userIds = users
        .map(user => user.id)
        .filter((userId): userId is string => Boolean(userId));

      const emails = users
        .map(user => user.email)
        .filter((email): email is string => Boolean(email));

      const dashboardUsers = await fetchRevokeAllDashboardAssignments(emails);

      await Promise.all([
        UserService.bulkPutReportingPermissions(
          userIds.map(userId => ({userId, permissions: []})),
        ),
        QuicksightService.putPermissionsBulk({
          selected: false,
          dashboardUsers,
        }),
      ]);
    },
    {
      onSuccess: (_, users) => {
        queryClient.invalidateQueries("usersPermissionQuery");
        users.forEach(user => {
          if (user.id) {
            queryClient.invalidateQueries(["useUserQuery", user.id]);
          }
        });
        invalidateQuicksightDashboardCachesForEmails(
          queryClient,
          users.map(user => user.email),
        );
        Alert.success(
          `Removed permissions from ${users.length} user${
            users.length === 1 ? "" : "s"
          }`,
        );
      },
      onError: async (resp: unknown) => {
        Alert.warning(
          await buildResponseErrorMessage(
            resp,
            "Failed to remove permissions from selected users",
          ),
        );
      },
    },
  );
};
